/* global chrome */
'use strict';

import * as domUtils from '../util/dom';
import Config from '../config';
import ChromecastInfoComponent from './chromecast/chromecast-info-component';
import debugUtil from '../util/debug';
import deviceUtil from '../util/device';

const Debug = debugUtil('chromecast');

const EVENT_NAMESPACE = 'chromecast';

const CHROMECAST_SDK_URL = 'https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1';

const instances = new Map();

let player = null;
let controller = null;

// Support Chrome on Desktop, and Chrome for Android.
const isChromecastSupported = () => (
    deviceUtil.isDesktopChrome() ||
    (window.chrome && deviceUtil.isAndroid())
);

const loadCastApi = () => {
    return new Promise((resolve) => {
        if (window['cast'] !== undefined) {
            resolve();
        } else if (window['__onGCastApiAvailable'] === undefined) {
            window['__onGCastApiAvailable'] = (isAvailable) => {
                if (isAvailable) {
                    resolve();
                }
            };
            domUtils.getScript(CHROMECAST_SDK_URL);
        }
    });
};


class ChromecastPlugin {
    constructor(api) {
        this.api = api;
        this.player = null;
        this.controllerListeners = [];
        this.controller = null;

        this.filter = null;

        this.chromecastInfoComponent = null;

        const buttonContainer = (Config.chromecast && Config.chromecast.buttonContainer) || 'top-right';

        this.castButton = document.createElement('button', 'google-cast-button');
        this.castButton.addEventListener('click', (e) => {
            Debug.log('Cast button clicked');
            e.stopPropagation();
        });
        this.castButton.addEventListener('touchend', (e) => {
            // Prevent ui.js from cancelling this event. The stopPropagation in
            // the click listener above is not enough because touchend occur
            // before click.
            e.stopPropagation();
        });
        this.api.insertButton(this.castButton, { container: buttonContainer });

        loadCastApi().then(() => {
            this.initializeCastApi();
        });
    }

    handleConnectedChanged() {
        if (this.player.isConnected) {
            this.api.data().allowNoUi = false;

            this.connectAPIFilter();

            this.transitionPlaybackToChromecast();
        } else {
            this.api.data().allowNoUi = true;

            this.disconnectAPIFilter();

            this.transitionPlaybackToBrowser();
        }
    }

    connectAPIFilter() {
        if (this.filter !== null) {
            return;
        }

        this.filter = {
            load: (videoId, autoplay, options) => {
                if (options.chromecast) {
                    return;
                }

                if (this.player.isConnected) {
                    this.loadVideo(videoId, autoplay, options);

                    return true;
                }
            }
        };

        this.api.filters.push(this.filter);
    }

    disconnectAPIFilter() {
        if (this.filter === null) {
            return;
        }

        const index = this.api.filters.indexOf(this.filter);
        if (index !== -1) {
            this.api.filters.splice(index, 1);
        }

        this.filter = null;
    }

    connectController() {
        this.controllerListeners.push({
            type: cast.framework.RemotePlayerEventType.IS_CONNECTED_CHANGED,
            handler: () => this.handleConnectedChanged()
        });

        this.controllerListeners.forEach(({ type, handler }) => {
            this.controller.addEventListener(type, handler);
        });
    }

    disconnectController() {
        while (this.controllerListeners.length > 0) {
            const { type, handler } = this.controllerListeners.shift();
            this.controller.removeEventListener(type, handler);
        }
    }

    transitionPlaybackToChromecast() {
        Debug.log('transitionPlaybackToChromecast');
        const startPosition = this.api.getCurrentTime();
        const videoId = this.api.data().videoId;

        if (this.player.isMediaLoaded) {
            Debug.log("Currently playing ", this.player.mediaInfo.contentId);
            this.loadVideo(videoId, false, { startPosition });
        } else {
            Debug.log('should load with autoplay');
            this.loadVideo(videoId, true, { startPosition });
        }
    }

    loadVideo(videoId, autoplay, options) {
        this.api.load(
            videoId,
            autoplay,
            Object.assign({}, options, {
                chromecast: {
                    player: this.player,
                    controller: this.controller
                }
            })
        );
    }

    transitionPlaybackToBrowser() {
        Debug.log('transitionPlaybackToBrowser');
        Debug.log(Object.assign({}, this.player.savedPlayerState));
        // TODO: Get contentId from player and check if it matches videoId.

        if (this.player.savedPlayerState === null) {
            return;
        }

        const videoId = this.api.data().videoId;
        const startPosition = this.player.savedPlayerState.currentTime;
        this.api.load(videoId, false, { startPosition, splash: false });
    }

    initializeCastApi() {
        Debug.log('initializeCastApi');

        // Config.chromecastReceiverId is legacy, and supported for backwards
        // compatibility.
        const receiverApplicationId =
            (Config.chromecast && Config.chromecast.receiverApplicationId) ||
            (Config.chromecastReceiverId);

        cast.framework.CastContext.getInstance().setOptions({
            receiverApplicationId,
            autoJoinPolicy: chrome.cast.AutoJoinPolicy.TAB_AND_ORIGIN_SCOPED
        });

        if (player === null) {
            player = new cast.framework.RemotePlayer();
        }
        if (controller === null) {
            controller = new cast.framework.RemotePlayerController(player);
        }

        this.player = player;
        this.controller = controller;

        this.chromecastInfoComponent = new ChromecastInfoComponent(
            this.player,
            this.controller,
            this.api
        );
        this.api.element().appendChild(this.chromecastInfoComponent.element);

        this.connectController();

        // If it is already connected we must setup UI directly.
        if (this.player.isConnected) {
            this.handleConnectedChanged();
        }
    }


    destroy() {
        Debug.log('Destroy');

        if (this.chromecastInfoComponent) {
            this.chromecastInfoComponent.destroy();
            this.chromecastInfoComponent = null;
        }

        this.disconnectController();
    }

}

export function create(api) {
    if (!isChromecastSupported()) {
        return;
    }

    if (instances.has(api)) {
        Debug.warn(
            `Chromecast plugin was already created for this api instance and has
            not been destroyed. The previous instance may leak memory and listeners.`
        );
    }

    instances.set(api, new ChromecastPlugin(api));
}

export function destroy(api) {
    if (instances.has(api)) {
        instances.get(api).destroy();
        instances.delete(api);
    }
}




// WEBPACK FOOTER //
// ./src/js/plugins/chromecast.js